import { useQuery } from "@tanstack/react-query";
import {
  getExpenseTypes,
  getReports,
  getExpenses,
  getExpenseDescriptionsSummary,
} from "../api/reports";
import { AxiosRequestConfig } from "axios";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { Expense } from "@/types/expense";
import { ExpenseType } from "@/types/expenseType";
import { ExpenseDescriptionDetail, Report } from "@/types/report";

interface UseReportsProps {
  config?: AxiosRequestConfig;
  enabled?: boolean;
}

export const useGetExpenseTypes = () => {
  return useQuery<ApiResponse<ExpenseType[]>, Error, ExpenseType[]>({
    queryKey: ["expensesTypes"],
    queryFn: () => getExpenseTypes(),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });
};

export const useGetExpenses = ({ config, enabled = true }: UseReportsProps) => {
  return useQuery<
    PaginatedApiResponse<Expense[]>,
    Error,
    { data: Expense[]; pagination: PaginationInfo }
  >({
    queryKey: ["expenses", config?.params],
    queryFn: () => getExpenses(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
    enabled: enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });
};

export const useGetExpensesOptions = ({
  config,
  enabled = true,
}: UseReportsProps) => {
  return useQuery<ApiResponse<Expense[]>, Error, Expense[]>({
    queryKey: ["expensesOptions", config?.params],
    queryFn: () => getExpenses(config),
    select: (response) => response.data,
    enabled: enabled,
  });
};

export const useGetExpenseDescriptionSummary = ({
  config,
  enabled = true,
}: UseReportsProps) => {
  return useQuery<
    PaginatedApiResponse<ExpenseDescriptionDetail[]>,
    Error,
    { data: ExpenseDescriptionDetail[]; pagination: PaginationInfo }
  >({
    queryKey: ["expenseDescriptionSummary", config?.params],
    queryFn: () => getExpenseDescriptionsSummary(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
    enabled: enabled,
  });
};

export const useGetExpenseDescriptionSummaryOptions = ({
  config,
  enabled = true,
}: UseReportsProps) => {
  return useQuery<
    ApiResponse<ExpenseDescriptionDetail[]>,
    Error,
    ExpenseDescriptionDetail[]
  >({
    queryKey: ["expenseDescriptionSummaryOptions", config?.params],
    queryFn: () => getExpenseDescriptionsSummary(config),
    select: (response) => response.data,
    enabled: enabled,
  });
};

export const useGetReports = ({ config, enabled = true }: UseReportsProps) => {
  return useQuery<Report>({
    queryKey: ["reports", config?.params],
    queryFn: () => getReports(config),
    enabled: enabled,
  });
};
